/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import {
  Button,
  Badge,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@repo/ui";
import { Filter, CircleX } from "lucide-react";
import KendoMultiselect from "ui/multiselect";
import type { MultiSelectChangeEvent } from "@progress/kendo-react-dropdowns";

interface DatePreset {
  id: string;
  name: string;
}

interface ResponseFiltersProps {
  DATE_PRESETS: DatePreset[];

  responseFilters: any;
  selectedCustomers: any[];
  selectedActivityTypes: any[];

  customers?: any[];
  ACTIVITY_TYPES: any[];

  setSelectedCustomers: (v: any[]) => void;
  setSelectedActivityTypes: (v: any[]) => void;

  updateResponseFilter: (key: string, value?: any) => void;

  syncResponseFiltersToUrl: (
    date?: string,
    customers?: string[],
    activityTypes?: string[],
  ) => void;

  handleApplyResponseFilters: () => void;
  handleClearResponseFilters: () => void;
}

export default function ResponseFilters({
  DATE_PRESETS,
  responseFilters,
  selectedCustomers,
  selectedActivityTypes,
  customers,
  ACTIVITY_TYPES,
  setSelectedCustomers,
  setSelectedActivityTypes,
  updateResponseFilter,
  syncResponseFiltersToUrl,
  handleApplyResponseFilters,
  handleClearResponseFilters,
}: ResponseFiltersProps) {
  return (
    <div className="flex gap-3 items-center">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="secondary">
            Response Filters <Filter size={16} className="ml-2" />
          </Button>
        </PopoverTrigger>

        <PopoverContent align="start" className="w-[80vw] p-4">
          <div className="grid grid-cols-3 gap-3">
            {/* Date */}
            <Select
              value={responseFilters.date?.id}
              onValueChange={(v) => {
                const selectedDate = DATE_PRESETS.find((d) => d.id === v);
                updateResponseFilter("date", selectedDate);

                syncResponseFiltersToUrl(
                  selectedDate?.id,
                  selectedCustomers.map((c) => c.customerId),
                  selectedActivityTypes.map((a) => a.id),
                );
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {DATE_PRESETS.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Customers */}
            <KendoMultiselect
              data={customers || []}
              textField="name"
              dataItemKey="customerId"
              value={selectedCustomers}
              onChange={(e: MultiSelectChangeEvent) => {
                const value = e.value as any[];
                setSelectedCustomers(value);

                syncResponseFiltersToUrl(
                  responseFilters.date?.id,
                  value.map((c) => c.customerId),
                  selectedActivityTypes.map((a) => a.id),
                );
              }}
              filterable
              placeholder="Select Customers"
            />

            {/* Activity Types */}
            <KendoMultiselect
              data={ACTIVITY_TYPES}
              textField="text"
              dataItemKey="id"
              value={selectedActivityTypes}
              onChange={(e: MultiSelectChangeEvent) => {
                const value = e.value as any[];
                setSelectedActivityTypes(value);

                syncResponseFiltersToUrl(
                  responseFilters.date?.id,
                  selectedCustomers.map((c) => c.customerId),
                  value.map((a) => a.id),
                );
              }}
              placeholder="Activity Types"
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={handleClearResponseFilters}>
              Clear
            </Button>

            <Button variant="secondary" onClick={handleApplyResponseFilters}>
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Active Filter Badges */}
      <div className="flex gap-2 flex-wrap">
        {responseFilters.date ? (
          <Badge variant="secondary">
            {responseFilters.date.name}
            <CircleX
              size={14}
              onClick={() => {
                updateResponseFilter("date");
              }}
            />
          </Badge>
        ) : null}

        {selectedCustomers.map((c) => (
          <Badge key={c.customerId} variant="secondary">
            {c.name}
            <CircleX
              size={14}
              onClick={() => {
                setSelectedCustomers(
                  selectedCustomers.filter(
                    (x) => x.customerId !== c.customerId,
                  ),
                );
              }}
            />
          </Badge>
        ))}

        {selectedActivityTypes.map((a) => (
          <Badge key={a.id} variant="secondary">
            {a.text}
            <CircleX
              size={14}
              onClick={() => {
                setSelectedActivityTypes(
                  selectedActivityTypes.filter((x) => x.id !== a.id),
                );
              }}
            />
          </Badge>
        ))}
      </div>
    </div>
  );
}
